import { OAuthStorage } from 'angular-oauth2-oidc'

/**
 * Hybrid OIDC storage.
 *
 * Session TOKENS (access/id/refresh + their expiry/claims) live in localStorage so they're shared across
 * same-origin tabs — a newly opened tab inherits the session instead of forcing a fresh login.
 *
 * The per-login-flow TRANSIENT values (`nonce`, `PKCE_verifier`) stay in sessionStorage, which is PER-TAB. If
 * they were shared too, two tabs running the OAuth code flow at the same moment (e.g. several tabs all
 * re-logging-in after a cross-tab logout) would overwrite each other's nonce/verifier, and the tab returning
 * from Identity would then fail `validateNonce` with "invalid_nonce_in_state". Keeping them per-tab isolates
 * concurrent flows while still sharing the resulting session.
 */
export class HybridOAuthStorage implements OAuthStorage {
  private static readonly TRANSIENT_KEYS = new Set<string>(['nonce', 'PKCE_verifier'])

  private storageFor(key: string): Storage {
    return HybridOAuthStorage.TRANSIENT_KEYS.has(key) ? sessionStorage : localStorage
  }

  getItem(key: string): string | null {
    return this.storageFor(key).getItem(key)
  }

  removeItem(key: string): void {
    this.storageFor(key).removeItem(key)
  }

  setItem(key: string, data: string): void {
    this.storageFor(key).setItem(key, data)
  }
}
